/**
 * UcExchanges — блок «Обмены (N)» UC-панели (screen-22, эталон renderX
 * js:205-222): строка — «−UC → +₽», имя, дата зарплаты «зп DD.MM»
 * (period_date — этой датой обмен падает в расчёт finance.ts), кто провёл.
 * Свои чипы периода (Все / 7 дней / Этот месяц) и оператора — независимы от
 * селектора Балансов (эталон tipE); оператор — серверным ?operator_id (УД5),
 * период — клиентски по created_at (эталон js:204-206 — дата ПРОВЕДЕНИЯ;
 * серверный ?from фильтрует period_date). Flash первой строки — после обмена.
 */
import { useState, type MouseEvent } from "react";
import { Dropdown } from "../../components/shared";
import type { UcAdmin } from "../../hooks/useUcAdmin";
import { NB, firstWord, fmtDD, fmtM, fmtUC } from "./ucFmt";
import cfg from "./config.module.css";
import dcss from "./dicts.module.css";
import pcss from "./prices.module.css";
import qcss from "./quests.module.css";
import ucss from "./uc.module.css";

/** Встречные стрелки шапки (эталон html:234). */
const XIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 7h13l-3-3M20 17H7l3 3" />
  </svg>
);

export function UcExchanges({ uc, onTip }: {
  uc: UcAdmin;
  onTip: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  const [pd, setPd] = useState<HTMLElement | null>(null);
  const [od, setOd] = useState<HTMLElement | null>(null);
  const rows = uc.exchanges;
  const flash = uc.flash?.key === "x" ? uc.flash : null;

  return (
    <div className={ucss.blk}>
      <div className={`${qcss.s17head} ${ucss.hd}`}>
        <span className={ucss.hcy}><XIco /></span>
        <h2 className={`${cfg.secT} ${cfg.cy}`}>Обмены ({rows.length})</h2>
        <button type="button" className={pcss.qmark} data-qmark="" aria-label="Справка" onClick={onTip}>
          <span>?</span>
        </button>
        <button
          type="button"
          className={`${dcss.fchip} ${ucss.chipW} ${uc.xPer.from ? dcss.on : ""}`}
          onClick={(e) => setPd(e.currentTarget)}
        >
          <span className={dcss.fv}>{uc.xPer.label}</span>
          <span className={dcss.chev}>▾</span>
        </button>
        <button
          type="button"
          className={`${dcss.fchip} ${ucss.chipW} ${uc.xOp ? dcss.on : ""}`}
          onClick={(e) => setOd(e.currentTarget)}
        >
          <span className={dcss.fv}>{uc.xOp ? firstWord(uc.nameOf(uc.xOp)) : "Все"}</span>
          <span className={dcss.chev}>▾</span>
        </button>
      </div>
      {rows.length ? (
        <div className={ucss.mcard}>
          {rows.map((r, i) => {
            const fl = flash && i === 0;
            const by = r.created_by === "system" ? "система" : firstWord(uc.nameOf(r.created_by) ?? r.created_by);
            return (
              <div key={fl ? `${r.id}:${flash.n}` : r.id} className={`${ucss.xrow} ${fl ? cfg.flash : ""}`}>
                <span className={`${ucss.xuc} ${qcss.num}`}>−{fmtUC(r.uc_amount)}{NB}UC</span>
                <span className={ucss.xarr}>→</span>
                <span className={`${ucss.xrub} ${qcss.num}`}>+₽{fmtM(r.money_amount)}</span>
                <span className={ucss.xname}>{firstWord(r.operator_name)}</span>
                <span className={`${ucss.xzp} ${qcss.num}`}>зп {fmtDD(r.period_date)}</span>
                <span className={ucss.xby}>{by}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={cfg.teaEmpty}>Обменов не было</div>
      )}
      <Dropdown
        open={!!pd}
        anchor={pd}
        multi={false}
        selectedIds={[uc.xPer.id]}
        items={uc.xPresets.map((p) => ({ id: p.id, label: p.label }))}
        onClose={() => setPd(null)}
        onCommit={(ids) => {
          const p = uc.xPresets.find((x) => x.id === ids[0]);
          if (p) uc.setXPer(p);
        }}
      />
      <Dropdown
        open={!!od}
        anchor={od}
        multi={false}
        filter
        selectedIds={uc.xOp ? [uc.xOp] : ["__all"]}
        items={[{ id: "__all", label: "Все" }, ...uc.balances.map((b) => ({ id: b.operator_id, label: b.operator_name }))]}
        onClose={() => setOd(null)}
        onCommit={(ids) => {
          const id = ids[0];
          if (id == null) return;
          uc.setXOp(id === "__all" ? null : String(id));
        }}
      />
    </div>
  );
}
